import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdHandshake } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import {
  normalizeCredentialsHint,
  toCredentialMessage,
} from "utils/credentialClientMessage";
import { computeExpiryIsoDate, getPlanById } from "constants/clientSlotPlans";
import BrokerServerField from "components/client/BrokerServerField";
import ConnectTradingAccountInstructions from "components/client/ConnectTradingAccountInstructions";
import SlaveAccountRowStatus from "components/client/SlaveAccountRowStatus";
import SlaveAccountRowToolbar from "components/client/SlaveAccountRowToolbar";
import { useClientDuplikiumSlaveRows } from "hooks/useClientDuplikiumSlaveRows";
import { readClientSession } from "utils/clientSession";
import { buildSlotsOwnedUrl } from "utils/subscriptionPath";
import { buildSlotExtensionPayUrl } from "utils/slotExtensionPay";
import { WELCOME_PROMO_CODE } from "constants/clientSlotPlans";
import SubscriptionBuySlotPanel from "components/client/SubscriptionBuySlotPanel";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_PLAN_ID = "bronze";
/** Lien FXPro — uniquement pour « Go to Sign Up » sur cette page (pas les boutons SignUp du site). */
const PATH1_PARTNER_BROKER_SIGNUP_URL =
  "https://direct-fxpro.com/en/partner/AlphaSniper";
const WHATSAPP_MANAGER_URL = String(
  process.env.REACT_APP_WHATSAPP_MANAGER_URL || "",
).trim();
const WHATSAPP_IB_TRANSFER_MESSAGE =
  "Hi, I already have a broker account and need help linking it under AlphaSniper's IB (partner).";

function whatsAppManagerHref() {
  if (!WHATSAPP_MANAGER_URL) return "";
  if (/[?&]text=/i.test(WHATSAPP_MANAGER_URL)) return WHATSAPP_MANAGER_URL;
  const join = WHATSAPP_MANAGER_URL.includes("?") ? "&" : "?";
  return `${WHATSAPP_MANAGER_URL}${join}text=${encodeURIComponent(WHATSAPP_IB_TRANSFER_MESSAGE)}`;
}
export default function SubscriptionPath1BrokerPartner() {
  const location = useLocation();
  const session = readClientSession();
  const {
    accounts,
    allAccounts,
    setAccounts,
    setAllAccounts,
    accountsLoading,
    accountsLoadError,
    loadAccounts,
    effectiveClientId,
  } = useClientDuplikiumSlaveRows(session, {
    subscriptionPath: "path1",
    pollMs: 60000,
  });
  const [accountType, setAccountType] = useState("MT5");
  const [accountNumber, setAccountNumber] = useState("");
  const [server, setServer] = useState("");
  const [tradingPassword, setTradingPassword] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [paidSlotsCount, setPaidSlotsCount] = useState(0);
  const [awaitingApprovalSlots, setAwaitingApprovalSlots] = useState([]);
  const [partnerEmail, setPartnerEmail] = useState("");
  const [partnerStatus, setPartnerStatus] = useState(""); // "", "pending", "approved", "rejected"
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const partnerVerified = partnerStatus === "approved";
  const [connectLoadingId, setConnectLoadingId] = useState(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [connectError, setConnectError] = useState("");
  const [linkNotice, setLinkNotice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [editForm, setEditForm] = useState({
    accountType: "MT5",
    accountNumber: "",
    server: "",
    tradingPassword: "",
  });

  useEffect(() => {
    let cancelled = false;
    const loadSlots = async () => {
      try {
        const res = await fetch(apiUrl(buildSlotsOwnedUrl("path1")), {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const n = Number(data?.paidSlotsCount);
        if (!cancelled && Number.isFinite(n) && n >= 0) {
          setPaidSlotsCount(n);
        }
        if (!cancelled && Array.isArray(data?.awaitingApprovalSlots)) {
          setAwaitingApprovalSlots(data.awaitingApprovalSlots);
        }
      } catch {
        /* API not available yet — paidSlotsCount stays at its current value. */
      }
    };
    void loadSlots();
    const shouldPoll =
      location.state?.paymentPendingApproval === true ||
      awaitingApprovalSlots.length > 0;
    const intervalId = shouldPoll
      ? window.setInterval(() => void loadSlots(), 12000)
      : null;
    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [
    effectiveClientId,
    location.state?.paymentPendingApproval,
    awaitingApprovalSlots.length,
  ]);

  const showPendingApproval =
    location.state?.paymentPendingApproval === true ||
    awaitingApprovalSlots.length > 0;

  const pendingSlotLabel = useMemo(() => {
    const fromState = String(location.state?.slotName || "").trim();
    const fromApi = awaitingApprovalSlots
      .map((s) => String(s.orderId || s.codification || "").trim())
      .filter(Boolean);
    if (fromState) return fromState;
    return fromApi[0] || "";
  }, [
    awaitingApprovalSlots,
    location.state?.slotName,
    location.state?.paymentPendingApproval,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const res = await fetch(apiUrl("/api/client/partner-broker-status"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled || !data?.ok) return;
        const latest = data.latest;
        if (latest) {
          setPartnerEmail((prev) => prev || latest.email || "");
          setPartnerStatus(String(latest.status || "").toLowerCase());
        }
      } catch {
        /* status endpoint not reachable yet — leave UI untouched */
      }
    };

    void fetchStatus();
    const intervalId = window.setInterval(() => {
      if (cancelled) return;
      void fetchStatus();
    }, 20000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [session?.clientId]);

  const usedSlotsGlobal = useMemo(
    () =>
      new Set(
        (allAccounts || accounts)
          .map((a) => Number(a.slot))
          .filter((s) => Number.isFinite(s) && s > 0),
      ),
    [allAccounts, accounts],
  );

  const availableSlots = useMemo(() => {
    const list = [];
    const need = Math.max(0, paidSlotsCount - accounts.length);
    let s = 1;
    while (list.length < need && s < 200) {
      if (!usedSlotsGlobal.has(s)) list.push(s);
      s += 1;
    }
    return list;
  }, [paidSlotsCount, accounts.length, usedSlotsGlobal]);

  useEffect(() => {
    if (availableSlots.length === 0) {
      if (selectedSlot !== "") setSelectedSlot("");
      return;
    }
    if (!availableSlots.includes(Number(selectedSlot))) {
      setSelectedSlot(String(availableSlots[0]));
    }
  }, [availableSlots, selectedSlot]);

  const canAddAccount = paidSlotsCount > 0 && availableSlots.length > 0;

  const handleVerifyPartnerEmail = async () => {
    setVerifyError("");
    setVerifyMessage("");
    const email = String(partnerEmail || "").trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setVerifyError("Please enter a valid e-mail address.");
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await fetch(apiUrl("/api/client/verify-partner-broker-email"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          email,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setPartnerStatus("");
        setVerifyError(
          "Partner-broker verification is not configured yet. Please try again later.",
        );
        return;
      }
      if (!res.ok || !data.ok) {
        setVerifyError(
          clientSafeMessage(
            data.message,
            "We could not submit this e-mail to the broker partner.",
          ),
        );
        return;
      }
      const nextStatus = String(data.status || data.row?.status || "pending").toLowerCase();
      setPartnerStatus(nextStatus);
      setVerifyMessage(
        clientSafeMessage(
          data.message,
          nextStatus === "approved"
            ? "E-mail verified with the broker partner."
            : "Your e-mail is pending approval by the team.",
        ),
      );
    } catch (e) {
      setVerifyError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canAddAccount) {
      setConnectError(
        paidSlotsCount === 0
          ? "You must buy a slot before adding a trading account."
          : "All your paid slots are already used.",
      );
      return;
    }
    const slotNum = Number(selectedSlot);
    if (!Number.isFinite(slotNum) || slotNum < 1) {
      setConnectError("Please select a slot.");
      return;
    }
    if (!accountNumber.trim()) {
      setConnectError("Please enter your account number.");
      return;
    }
    if (!server.trim()) {
      setConnectError("Please select or enter your broker server.");
      return;
    }
    if (!tradingPassword.trim()) {
      setConnectError("Please enter your trading password.");
      return;
    }

    setAddingAccount(true);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-save-slave-draft"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: slotNum,
          accountNumber: accountNumber.trim(),
          platform: accountType,
          server: server.trim(),
          tradingPassword: tradingPassword.trim(),
          subscriptionPath: "path1",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setConnectError(
          clientSafeMessage(data.message, "Could not save your trading account."),
        );
        return;
      }
      const saved = data.account || {};
      const optimistic = {
        id: saved.id || `slot-${slotNum}`,
        slot: Number(saved.slot) || slotNum,
        subscriptionPath: saved.subscriptionPath || "path1",
        accountType: saved.accountType || accountType,
        accountNumber: saved.accountNumber || accountNumber.trim(),
        server: saved.server || server.trim(),
        tradingPassword: tradingPassword.trim(),
        tradingPasswordMasked: "••••••••••",
        linkedToCopier: false,
        brokerConnected: false,
        brokerState: null,
        balanceDisplay: null,
        credentialsHint: null,
        draftSaved: true,
        copierDisplayName: "",
        tradeCopierAccountId: null,
      };
      setAccounts((prev) => {
        const without = prev.filter((a) => Number(a.slot) !== optimistic.slot);
        return [...without, optimistic].sort(
          (a, b) => Number(a.slot) - Number(b.slot),
        );
      });
      setAllAccounts((prev) => {
        const without = (prev || []).filter(
          (a) => Number(a.slot) !== optimistic.slot,
        );
        return [...without, optimistic].sort(
          (a, b) => Number(a.slot) - Number(b.slot),
        );
      });
      setAccountNumber("");
      setServer("");
      setTradingPassword("");
      setConnectError("");
      setLinkNotice(
        "Account saved. Connecting to the system automatically…",
      );
      window.setTimeout(() => {
        void handleConnectToSystem(optimistic.id, {
          fromAdd: true,
          account: optimistic,
        });
      }, 400);
    } catch (err) {
      setConnectError(clientSafeMessage(err?.message, "Could not save your trading account."));
    } finally {
      setAddingAccount(false);
    }
  };

  const handleConnectToSystem = async (accountId, opts = {}) => {
    if (editingId != null) {
      setConnectError("Please save before connecting.");
      return;
    }
    if (!accountId || connectLoadingId) return;
    const target =
      opts.account ||
      accounts.find((a) => a.id === accountId) ||
      null;
    if (!target || target.linkedToCopier) return;
    const plan = getPlanById(DEFAULT_PLAN_ID);
    if (!plan) {
      setConnectError("Plan configuration missing.");
      return;
    }
    setConnectLoadingId(accountId);
    setConnectError("");
    setLinkNotice(
      opts.fromAdd
        ? "Connecting your account to the system…"
        : "Connecting to the system…",
    );
    const activatingDate = new Date().toISOString().slice(0, 10);
    const expiringDate = computeExpiryIsoDate(activatingDate, plan.months);
    const maxAttempts = 4;
    const slotNum = Number(target.slot) || 1;

    try {
      let lastMessage = "";
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const pwd = target.tradingPassword || "";
        const res = await fetch(apiUrl("/api/client/copy-provision-slave"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: effectiveClientId ?? session?.clientId ?? null,
            slot: slotNum,
            accountNumber: target.accountNumber,
            platform: target.accountType || "MT5",
            server: target.server,
            tradingPassword: pwd,
            planId: plan.id,
            activatingDate,
            expiringDate,
            connectOnly: false,
            subscriptionPath: "path1",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.skipped) {
          setConnectError(
            clientSafeMessage(
              data.message,
              "Please sign in with your account to connect your trading account.",
            ),
          );
          setLinkNotice("");
          return;
        }
        if (res.ok && data.ok && data.linking) {
          setConnectError("");
          setLinkNotice(
            String(data.message || "").trim() ||
              "Linking started. Wait 1–2 minutes, then click Refresh until status shows Connected.",
          );
          for (let i = 0; i < 18; i += 1) {
            await new Promise((r) => window.setTimeout(r, 5000));
            await loadAccounts();
            setLinkNotice(
              `Linking in progress… refresh ${i + 1}/18. Click Refresh if status is not Connected yet.`,
            );
          }
          setLinkNotice(
            "If still not Connected: Edit → re-enter password → Connect again, then Refresh.",
          );
          return;
        }
        const rawMsg = String(data.message || "");
        const busy =
          /request limit|rate limit|busy|too many/i.test(rawMsg) ||
          /service is busy/i.test(clientSafeMessage(rawMsg, ""));
        if ((!res.ok || !data.ok) && busy && attempt < maxAttempts) {
          const waitSec = 60;
          setConnectError("");
          for (let left = waitSec; left >= 1; left -= 1) {
            setLinkNotice(
              `The system is busy. Retrying automatically in ${left}s… (${attempt}/${maxAttempts})`,
            );
            await new Promise((r) => window.setTimeout(r, 1000));
          }
          setLinkNotice(
            `The system is busy. Retrying now… (${attempt}/${maxAttempts})`,
          );
          continue;
        }
        if (!res.ok || !data.ok) {
          lastMessage = rawMsg;
          setConnectError(
            toCredentialMessage(
              rawMsg,
              "We could not connect your trading account. Check your credentials and try again.",
            ),
          );
          setLinkNotice("");
          return;
        }
        if (data.copySettingsWarning) {
          console.warn(
            "[subscription] copy settings (Duplikium):",
            data.copySettingsWarning,
          );
        }
        await loadAccounts();
        if (data.brokerConnected === true) {
          setLinkNotice(
            "Connected. Your account is on the system — use Refresh if the balance is not visible yet.",
          );
        } else {
          setLinkNotice(
            "Account linked to the system. Status CONNECTED usually appears in 1–3 minutes — click Refresh. Typical wait after Connect: about 1–2 minutes.",
          );
        }
        setConnectError("");
        return;
      }
      setConnectError(
        toCredentialMessage(
          lastMessage,
          "The service is busy. Please wait about one minute and try Connect again.",
        ),
      );
      setLinkNotice("");
    } catch (err) {
      const raw = String(err?.message || "");
      setConnectError(
        /failed to fetch|networkerror|load failed/i.test(raw)
          ? "The server took too long (or the connection dropped). Wait 1–2 minutes, click Refresh — if still not Connected, use Edit to re-enter the password then Connect again."
          : clientSafeMessage(raw, "Connection failed."),
      );
      setLinkNotice("");
    } finally {
      setConnectLoadingId(null);
    }
  };

  const startEditing = (row) => {
    setDeleteConfirmId(null);
    setEditingId(row.id);
    setEditForm({
      accountType: row.accountType || "MT5",
      accountNumber: row.accountNumber || "",
      server: row.server || "",
      tradingPassword: row.tradingPassword || "",
    });
    setConnectError("");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({
      accountType: "MT5",
      accountNumber: "",
      server: "",
      tradingPassword: "",
    });
  };

  const handleEditCredentials = async (accountId) => {
    if (!accountId || actionLoadingId || connectLoadingId) return;
    const target = accounts.find((a) => a.id === accountId);
    if (!target) return;

    const nextType = String(editForm.accountType || "").trim().toUpperCase();
    const accountNumberTrim = String(editForm.accountNumber || "").trim();
    const serverTrim = String(editForm.server || "").trim();
    const passwordTrim = String(editForm.tradingPassword || "").trim();

    if (nextType !== "MT4" && nextType !== "MT5") {
      setConnectError("Type must be MetaTrader 4 or MetaTrader 5.");
      return;
    }
    if (!accountNumberTrim || !serverTrim || !passwordTrim) {
      setConnectError("Type, Account Number, Server and Password are required.");
      return;
    }

    setActionLoadingId(accountId);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-update-slave-credentials"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: Number(target.slot),
          accountNumber: accountNumberTrim,
          platform: nextType,
          server: serverTrim,
          tradingPassword: passwordTrim,
          subscriptionPath: "path1",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setConnectError(
          clientSafeMessage(
            data.message,
            "Could not update this account. Try Add again or Connect to system.",
          ),
        );
        return;
      }
      if (!res.ok || !data.ok) {
        setConnectError(
          toCredentialMessage(data.message, "Could not update your account."),
        );
        return;
      }

      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountId
            ? {
                ...acc,
                accountType: nextType,
                accountNumber: accountNumberTrim,
                server: serverTrim,
                tradingPassword: passwordTrim,
                tradingPasswordMasked: "•".repeat(Math.min(passwordTrim.length, 12)),
                tradeCopierAccountId:
                  data.tradeCopierAccountId ?? acc.tradeCopierAccountId ?? null,
                linkedToCopier: true,
                brokerConnected: data.brokerConnected === true,
                brokerState:
                  data.brokerState != null ? String(data.brokerState) : null,
                balanceDisplay: data.balanceDisplay ?? null,
                credentialsHint: data.credentialsHint ?? null,
              }
            : acc,
        ),
      );
      cancelEditing();
      if (data.copySettingsWarning) {
        console.warn(
          "[subscription] copy settings after save (Duplikium):",
          data.copySettingsWarning,
        );
      }
      setConnectError("Allow at least 1 min for system before connecting.");
    } catch (err) {
      setConnectError(
        clientSafeMessage(err?.message, "Credentials update failed."),
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const refreshBrokerStatus = async (accountId) => {
    const target = accounts.find((a) => a.id === accountId);
    if (!target?.slot) return;
    setStatusLoadingId(accountId);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-slave-broker-status"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: Number(target.slot),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setConnectError(
          toCredentialMessage(data.message, "Status refresh failed."),
        );
        return;
      }
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountId
            ? {
                ...acc,
                brokerConnected: data.brokerConnected === true,
                brokerState:
                  data.brokerState != null ? String(data.brokerState) : null,
                tradeCopierAccountId:
                  data.tradeCopierAccountId ?? acc.tradeCopierAccountId ?? null,
                linkedToCopier: true,
                balanceDisplay: data.balanceDisplay ?? null,
                credentialsHint: data.credentialsHint ?? null,
                activatingDate: data.activatingDate ?? acc.activatingDate ?? null,
                expiringDate: data.expiringDate ?? acc.expiringDate ?? null,
              }
            : acc,
        ),
      );
    } catch (e) {
      setConnectError(toCredentialMessage(e?.message, "Status refresh failed."));
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!accountId || actionLoadingId || connectLoadingId) return;
    const target = accounts.find((a) => a.id === accountId);
    if (!target) return;

    setActionLoadingId(accountId);
    setConnectError("");
    try {
      if (target.slot != null) {
        const res = await fetch(apiUrl("/api/client/copy-delete-slave"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: effectiveClientId ?? session?.clientId ?? null,
            slot: Number(target.slot),
            tradeCopierAccountId: target.tradeCopierAccountId ?? null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          setConnectError(
            clientSafeMessage(data.message, "Could not remove this account."),
          );
          return;
        }
      }
      setAccounts((prev) => prev.filter((acc) => acc.id !== accountId));
      if (deleteConfirmId === accountId) setDeleteConfirmId(null);
      await loadAccounts();
    } catch (err) {
      setConnectError(clientSafeMessage(err?.message, "Delete failed."));
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex items-center gap-2">
          <MdHandshake className="h-5 w-5 text-green-600 dark:text-green-300" />
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Path 1: Broker partner
          </h2>
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Broker partner onboarding guide.</p>

        <ol className="mt-5 space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm dark:border-white/10 dark:bg-navy-900/40">
          <li>
            <span className="font-semibold text-navy-700 dark:text-white">
              1. Sign up at the broker partner.
            </span>
            <ul className="mt-2 space-y-2 pl-4">
              <li>
                <span className="font-semibold text-navy-700 dark:text-white">a.</span>{" "}
                Use this link to sign up:{" "}
                <a
                  href={PATH1_PARTNER_BROKER_SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
                >
                  Go to Sign Up
                </a>
              </li>
              <li>
                <span className="font-semibold text-navy-700 dark:text-white">b.</span>{" "}
                Verify your account.
              </li>
              <li>
                <span className="font-semibold text-navy-700 dark:text-white">c.</span>{" "}
                Create an account on MetaTrader 4 or MetaTrader 5.
              </li>
              <li>
                <span className="font-semibold text-navy-700 dark:text-white">d.</span>{" "}
                Deposit the minimum required.
                <div className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                  Minimum required Leverage 1:1000 = USD 200.
                </div>
              </li>
              <li>
                <span className="font-semibold text-navy-700 dark:text-white">e.</span>{" "}
                Add your e-mail of subscription and tape it here, and click{" "}
                <strong>Verify me</strong>.
                <div className="mt-2 max-w-lg rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs leading-relaxed text-emerald-950 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
                  <p className="font-medium">
                    <strong>Already have an FXPro account?</strong> It must be
                    registered under our partner (IB) link. If you signed up
                    elsewhere, ask our account manager to move your account under
                    AlphaSniper&apos;s IB before you verify your e-mail here.
                  </p>
                  {whatsAppManagerHref() ? (
                    <a
                      href={whatsAppManagerHref()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#1ebe57]"
                    >
                      <FaWhatsapp className="h-4 w-4 shrink-0" aria-hidden />
                      Contact us on WhatsApp
                    </a>
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    value={partnerEmail}
                    onChange={(e) => {
                      setPartnerEmail(e.target.value);
                      if (verifyError) setVerifyError("");
                      if (verifyMessage) setVerifyMessage("");
                    }}
                    placeholder="you@example.com"
                    className="h-9 w-full max-w-[15rem] rounded-lg border border-gray-200 bg-white px-2.5 text-xs text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                  <button
                    type="button"
                    disabled={verifyLoading}
                    onClick={handleVerifyPartnerEmail}
                    className="h-9 shrink-0 rounded-lg bg-brand-500 px-3 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {verifyLoading ? "…" : partnerVerified ? "Verified" : "Verify me"}
                  </button>
                  {partnerStatus === "approved" ? (
                    <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-green-700 dark:bg-green-500/15 dark:text-green-300">
                      Status: Approved
                    </span>
                  ) : partnerStatus === "rejected" ? (
                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-700 dark:bg-red-500/15 dark:text-red-300">
                      Status: Rejected
                    </span>
                  ) : partnerStatus === "pending" ? (
                    <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-200">
                      Status: Pending
                    </span>
                  ) : null}
                </div>
                {verifyError ? (
                  <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                    {verifyError}
                  </p>
                ) : partnerStatus === "approved" ? (
                  <p className="mt-2 text-xs font-medium text-green-700 dark:text-green-300">
                    {verifyMessage || "E-mail verified with the broker partner."}
                  </p>
                ) : partnerStatus === "pending" ? (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-200">
                    {verifyMessage ||
                      "Your e-mail is pending approval by the team."}
                  </p>
                ) : verifyMessage ? (
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    {verifyMessage}
                  </p>
                ) : null}
              </li>
            </ul>
          </li>
          <SubscriptionBuySlotPanel
            subscriptionPath="path1"
            promoCode={WELCOME_PROMO_CODE}
            stepNumber={2}
          />
          <li>
            <span className="font-semibold text-navy-700 dark:text-white">
              3. Add your trading account.
            </span>
          </li>
        </ol>

        <ConnectTradingAccountInstructions />

        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm shadow-sm dark:border-white/10 dark:bg-navy-900/50">
          <span className="font-semibold text-navy-700 dark:text-white">
            Slot: {paidSlotsCount > 0 ? (
              <span className="ml-1 inline-flex items-center gap-1">
                <span className="rounded-md bg-brand-50 px-2 py-0.5 font-bold text-brand-700 dark:bg-brand-500/15 dark:text-brand-200">
                  {accounts.length}/{paidSlotsCount}
                </span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  used / paid
                </span>
              </span>
            ) : showPendingApproval ? (
              <span className="ml-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                Waiting for Admin Approval
                {pendingSlotLabel ? ` (${pendingSlotLabel})` : ""}
              </span>
            ) : (
              <span className="ml-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-200">
                None
              </span>
            )}
          </span>
        </div>

        {showPendingApproval && paidSlotsCount === 0 ? (
          <div className="mt-4 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            Payment received — your slot is <strong>Waiting for Admin Approval</strong>
            {pendingSlotLabel ? (
              <>
                {" "}
                (<span className="font-mono">{pendingSlotLabel}</span>)
              </>
            ) : null}
            . You cannot connect a trading account until an administrator verifies the
            payment and approves it.
          </div>
        ) : null}

        {paidSlotsCount === 0 && !showPendingApproval ? (
          <div className="mt-4 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            You must create a slot before adding a trading account. Click{" "}
            <strong>Create a slot</strong> above, then <strong>Pay</strong>.
          </div>
        ) : null}

        <form onSubmit={handleAdd} className="mt-6 space-y-4">
          <h3 className="text-base font-bold text-navy-700 dark:text-white">Add Account</h3>
          <fieldset
            disabled={!canAddAccount}
            className="grid grid-cols-1 gap-3 lg:grid-cols-6 disabled:opacity-60"
          >
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Slot
              </label>
              <select
                value={selectedSlot}
                onChange={(e) => setSelectedSlot(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 disabled:cursor-not-allowed dark:border-white/10 dark:bg-navy-900 dark:text-white"
              >
                {availableSlots.length === 0 ? (
                  <option value="">—</option>
                ) : (
                  availableSlots.map((n) => (
                    <option key={n} value={n}>
                      Slot {n}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Type
              </label>
              <select
                value={accountType}
                onChange={(e) => {
                  setAccountType(e.target.value);
                  setServer("");
                }}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              >
                <option value="MT4">MetaTrader4</option>
                <option value="MT5">MetaTrader5</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Account Number
              </label>
              <input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                placeholder="Enter account number"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Server
              </label>
              <BrokerServerField
                fxproOnly
                platform={accountType}
                value={server}
                onChange={setServer}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Trading Password
              </label>
              <input
                type="password"
                value={tradingPassword}
                onChange={(e) => setTradingPassword(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                placeholder="Enter trading password"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={addingAccount || !canAddAccount}
                className="h-11 w-full rounded-xl bg-brand-500 px-4 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {addingAccount ? "Saving…" : "Add"}
              </button>
            </div>
          </fieldset>
        </form>

        {connectError || accountsLoadError ? (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">
            {clientSafeMessage(
              connectError || accountsLoadError,
              "Something went wrong. Please try again.",
            )}
          </p>
        ) : null}
        {linkNotice ? (
          <p
            className="mt-4 text-sm text-emerald-700 dark:text-emerald-300"
            role="status"
          >
            {linkNotice}
          </p>
        ) : null}

        {accountsLoading && accounts.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading your accounts…
          </p>
        ) : null}

        {accounts.length > 0 ? (
          <div className="mt-6">
            <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              Added accounts
            </h4>
            <ul className="space-y-2">
              {accounts.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-white/10 dark:bg-navy-900/50"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 overflow-x-auto py-0.5 [&_input]:box-border [&_input]:h-9 [&_select]:box-border [&_select]:h-9">
                    {editingId === row.id ? (
                      <>
                        <select
                          value={editForm.accountType}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              accountType: e.target.value,
                              server: "",
                            }))
                          }
                          className="h-9 shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                        >
                          <option value="MT4">MetaTrader4</option>
                          <option value="MT5">MetaTrader5</option>
                        </select>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          S{row.slot}
                        </span>
                        <input
                          value={editForm.accountNumber}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, accountNumber: e.target.value }))
                          }
                          className="h-9 w-[115px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                          placeholder="Account number"
                        />
                        <BrokerServerField
                          fxproOnly
                          variant="compact"
                          platform={editForm.accountType}
                          value={editForm.server}
                          onChange={(v) =>
                            setEditForm((p) => ({ ...p, server: v }))
                          }
                        />
                        <input
                          type="password"
                          value={editForm.tradingPassword}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, tradingPassword: e.target.value }))
                          }
                          className="h-9 w-[100px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-0 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                          placeholder="Trading password"
                        />
                        <SlaveAccountRowToolbar
                          row={row}
                          editingId={editingId}
                          actionLoadingId={actionLoadingId}
                          connectLoadingId={connectLoadingId}
                          deleteConfirmId={deleteConfirmId}
                          onEdit={() => startEditing(row)}
                          onSave={() => handleEditCredentials(row.id)}
                          onCancelEdit={cancelEditing}
                          onDeleteClick={() => {
                            setEditingId(null);
                            setDeleteConfirmId(row.id);
                          }}
                          onDeleteConfirm={() => handleDeleteAccount(row.id)}
                          onDeleteCancel={() => setDeleteConfirmId(null)}
                        />
                      </>
                    ) : (
                      <>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          {row.accountType === "MT4" ? "MetaTrader4" : "MetaTrader5"}
                        </span>
                        <span className="rounded-md bg-brand-50 px-2 py-0.5 font-mono text-xs font-bold text-brand-700 dark:bg-brand-500/15 dark:text-brand-200">
                          {row.copierDisplayName || `S${row.slot}`}
                        </span>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          S{row.slot}
                        </span>
                        <span className="shrink-0 font-medium text-navy-800 dark:text-white">
                          {row.accountNumber}
                        </span>
                        <span className="shrink-0 text-gray-600 dark:text-gray-400">{row.server}</span>
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-500">
                          {row.tradingPasswordMasked}
                        </span>
                        <SlaveAccountRowToolbar
                          row={row}
                          editingId={editingId}
                          actionLoadingId={actionLoadingId}
                          connectLoadingId={connectLoadingId}
                          deleteConfirmId={deleteConfirmId}
                          canExtend={Boolean(row.expiringDate)}
                          extendTo={buildSlotExtensionPayUrl({
                            slot: row.slot,
                            codification:
                              row.copierDisplayName ||
                              `${effectiveClientId ?? session?.clientId ?? ""}S${row.slot}`,
                            subscriptionPath: "path1",
                            promoCode: WELCOME_PROMO_CODE,
                            currentExpiringDate: row.expiringDate,
                          })}
                          onEdit={() => startEditing(row)}
                          onSave={() => handleEditCredentials(row.id)}
                          onCancelEdit={cancelEditing}
                          onDeleteClick={() => {
                            setEditingId(null);
                            setDeleteConfirmId(row.id);
                          }}
                          onDeleteConfirm={() => handleDeleteAccount(row.id)}
                          onDeleteCancel={() => setDeleteConfirmId(null)}
                        />
                      </>
                    )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-2 dark:border-white/10">
                      <SlaveAccountRowStatus
                        row={row}
                        statusLoadingId={statusLoadingId}
                        onRefresh={refreshBrokerStatus}
                        variant="footer"
                      />
                      {!row.subscriptionExpired &&
                      !row.brokerConnected &&
                      !(row.linkedToCopier || row.tradeCopierAccountId) ? (
                        <button
                          type="button"
                          disabled={
                            connectLoadingId === row.id ||
                            editingId === row.id ||
                            statusLoadingId === row.id
                          }
                          onClick={() => handleConnectToSystem(row.id)}
                          className="rounded-lg bg-navy-800 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-navy-900 disabled:opacity-60 dark:bg-white/15 dark:hover:bg-white/25"
                        >
                          {connectLoadingId === row.id ? "Connecting…" : "Connect to system"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {(row.linkedToCopier || row.tradeCopierAccountId) &&
                  !row.brokerConnected &&
                  editingId !== row.id &&
                  row.credentialsHint ? (
                    <p className="border-t border-gray-200 pt-2 text-center text-base font-bold leading-snug text-red-600 dark:border-white/10 dark:text-red-400">
                      {normalizeCredentialsHint(row.credentialsHint)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Before deleting or editing the account, click Refresh.
          </p>
          <Link
            to="/client/subscription"
            className="inline-block text-sm font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Back to Subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
